import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { mountStoreDevtool } from 'simple-zustand-devtools'
import { get, set, del } from 'idb-keyval'
import createActionCableSlice from './slices/actionCableSlice'
import createAuthSlice from './slices/authSlice'
import createBankAccountSlice from './slices/bankAccountSlice'
import createBlackbookSlice from './slices/blackbookSlice'
import createBranchSlice from './slices/branchSlice'
import createDealerSlice from './slices/dealerSlice'
import createDealerUnitsSlice from './slices/dealerUnitsSlice'
import createGoogleSlice from './slices/googleSlice'
import createNapSlice from './slices/napSlice'
import createPaymentSlice from './slices/paymentSlice'
import createRemindersSlice from './slices/remindersSlice'
import createRiskMitigationSlice from './slices/riskMitigationSlice'
import createTransferSlice from './slices/transferSlice'
import createUnitsSlice from './slices/unitsSlice'
import createUserSlice from './slices/userSlice'
import createDealersSlice from './slices/dealersSlice'
import createDailyOpsSlice from './slices/dailyOpsSlice'
import resetAllSlices from './slices/resetAllSlices'
import createBusinessDevSlice from './slices/businessDevSlice'
import createApplicationsSlice from './slices/applicationsSlice'
import createNotesSlice from './slices/notesSlice'
import createPeopleSlice from './slices/peopleSlice'
import createNotificationsSlice from './slices/notificationsSlice'
import createDealerDocumentsSlice from './slices/dealerDocumentsSlice'
import createTouchesSlice from './slices/touchesSlice'
import createInAppMessagingSlice from './slices/inAppMessagingSlice'
import createPushNotificationsSlice from './slices/pushNotificationsSlice'
import createDealersListSlice from './slices/dealersListSlice'
import createCouponsSlice from './slices/couponsSlice'
import createHubspotSlice from './slices/hubspotSlice'
import createUnitFilesSlice from './slices/unitFilesSlice'
import createErrorSlice from './slices/errorSlice'
import createFieldAuditsSlice from './slices/fieldAuditsSlice'
import createRequestQueueSlice from './slices/requestQueueSlice'
import createDealerPlacesSlice from './slices/dealerPlacesSlice'
import createCarpoolSlice from './slices/carpoolSlice'
import createTrailersSlice from './slices/trailersSlice'

// Transient UI flags that must not persist across refreshes. If one of these
// loading flags is true when the page is closed mid-request (so the action's
// finally block never runs), persisting it would leave the related button stuck
// rendering a spinner on the next load.
const transientUiFlags = ['markingUnitSold', 'markingUnitUnSold', 'creatingUnitFile']

const nonPersistedKeys = ['consumer', 'actionButton', 'submittingNap', ...transientUiFlags]

const storage = {
  getItem: async (name) => {
    return (await get(name)) || null
  },
  setItem: async (name, value) => {
    await set(name, value)
  },
  removeItem: async (name) => {
    await del(name)
  },
}

const useStore = create(
  persist(
    (...a) => ({
      // When adding new slices, be sure to add a reset slice method to the resetAllSlices file
      // so that data is cleared on user logout.
      ...createActionCableSlice(...a),
      ...createApplicationsSlice(...a),
      ...createAuthSlice(...a),
      ...createBankAccountSlice(...a),
      ...createErrorSlice(...a),
      ...createBlackbookSlice(...a),
      ...createBranchSlice(...a),
      ...createBusinessDevSlice(...a),
      ...createCouponsSlice(...a),
      ...createDailyOpsSlice(...a),
      ...createDealerDocumentsSlice(...a),
      ...createDealersListSlice(...a),
      ...createDealerSlice(...a),
      ...createDealersSlice(...a),
      ...createDealerPlacesSlice(...a),
      ...createDealerUnitsSlice(...a),
      ...createFieldAuditsSlice(...a),
      ...createGoogleSlice(...a),
      ...createHubspotSlice(...a),
      ...createNapSlice(...a),
      ...createNotesSlice(...a),
      ...createNotificationsSlice(...a),
      ...createPaymentSlice(...a),
      ...createPeopleSlice(...a),
      ...createPushNotificationsSlice(...a),
      ...createRemindersSlice(...a),
      ...createRequestQueueSlice(...a),
      ...createRiskMitigationSlice(...a),
      ...createTouchesSlice(...a),
      ...createTransferSlice(...a),
      ...createUnitFilesSlice(...a),
      ...createUnitsSlice(...a),
      ...createUserSlice(...a),
      ...createInAppMessagingSlice(...a),
      ...createCarpoolSlice(...a),
      ...createTrailersSlice(...a),
      ...resetAllSlices(...a)
    }),
    {
      name: 'data-storage',
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([key, value]) => !nonPersistedKeys.includes(key) && typeof value !== 'function'
          )
        ),
      storage: storage,
      // Strip transient UI flags from any previously persisted payload so users
      // who already have a stuck flag (e.g. markingUnitSold: true) get reset on
      // their next load rather than rehydrating the stale value.
      merge: (persistedState, currentState) => {
        const sanitizedPersistedState = { ...(persistedState ?? {}) }
        transientUiFlags.forEach((flag) => delete sanitizedPersistedState[flag])
        return { ...currentState, ...sanitizedPersistedState }
      },
    }
  )
)

export default useStore

if (process.env.NODE_ENV === 'development') {
  mountStoreDevtool('Store', useStore);
}